import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import StatsOverlay from './StatsOverlay';
import CompanionStatsModal from './CompanionStatsModal';
import VideoRewardModal from './VideoRewardModal';

export interface PlayerStatsProps {
  onOpenSettings: () => void;
  onOpenStatsOverlay?: () => void;
  handleGemClick: () => void;
}

export const PlayerStats: React.FC<PlayerStatsProps> = ({ onOpenSettings, onOpenStatsOverlay, handleGemClick }) => {
  const { 
    coins, 
    gems, 
    playerLevel, 
    powerUps, 
    companions,
    getLevelProgress,
    getTotalCoinsPerSecond,
    updatePowerUps,
    updateConquests,
    formatNumber,
    getMaxCompanionSlots
  } = useGameStore();

  const [showCompanions, setShowCompanions] = useState(false);
  const [showPowerUps, setShowPowerUps] = useState(false);
  const [showStatsOverlay, setShowStatsOverlay] = useState(false);
  const [selectedCompanion, setSelectedCompanion] = useState<any>(null);
  const [selectedCompanionSlot, setSelectedCompanionSlot] = useState<number>(0);
  const [gemVideoCooldown, setGemVideoCooldown] = useState<number | null>(null);

  // Prüfe Cooldown beim Laden
  useEffect(() => {
    const savedCooldown = localStorage.getItem('gemVideoCooldown');
    if (savedCooldown) {
      const cooldownTime = parseInt(savedCooldown);
      const now = Date.now();
      if (now < cooldownTime) {
        setGemVideoCooldown(cooldownTime);
      } else {
        localStorage.removeItem('gemVideoCooldown');
      }
    }
  }, []);

  // Cooldown-Timer
  useEffect(() => {
    if (gemVideoCooldown) {
      const interval = setInterval(() => {
        const now = Date.now();
        if (now >= gemVideoCooldown) {
          setGemVideoCooldown(null);
          localStorage.removeItem('gemVideoCooldown');
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [gemVideoCooldown]);

  // Hilfsfunktion für Cooldown-Anzeige
  const getCooldownTime = () => {
    if (!gemVideoCooldown) return null;
    const now = Date.now();
    const remaining = Math.max(0, gemVideoCooldown - now);
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const isGemVideoAvailable = !gemVideoCooldown || Date.now() >= gemVideoCooldown;

  // Berechne die Edelstein-Belohnung für Video-Werbung
  const calculateGemReward = () => {
    let gemReward = 500; // Mindestbelohnung
    
    if (gems > 5000) {
      gemReward = Math.floor(gems * 2); // 2x der aktuellen Diamanten
    }
    
    return gemReward;
  };

  const levelProgress = getLevelProgress();
  const coinsPerSecond = getTotalCoinsPerSecond();

  // Update power-ups and conquests every second
  useEffect(() => {
    const interval = setInterval(() => {
      updatePowerUps();
      updateConquests();
    }, playerLevel > 1000 ? 2000 : 1000); // Langsamere Updates bei hohen Levels

    return () => clearInterval(interval);
  }, [updatePowerUps, updateConquests, playerLevel]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const activePowerUpsCount = Object.values(powerUps || {}).filter(powerUp => powerUp?.isActive).length;
  const activeCompanionsCount = companions?.active?.filter(c => c).length || 0;

  return (
    <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 mb-4 sm:mb-6 sm:bg-white/95 sm:backdrop-blur-sm">

      {/* Mobile-optimierte Hauptstatistiken */}
      <div className="space-y-3 sm:space-y-4">
        
        {/* Level & XP - Kompakt für Mobile - Klickbar für detaillierte Statistiken */}
        <motion.div 
          className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-3 cursor-pointer hover:from-purple-100 hover:to-indigo-100 transition-colors"
          onClick={() => {
            if (onOpenStatsOverlay) onOpenStatsOverlay();
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          title="📊 Klicken für detaillierte Statistiken"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xl sm:text-2xl">⭐</span>
              <div>
                <div className="text-base sm:text-lg font-bold text-purple-700">Level {formatNumber(playerLevel || 1)}</div>
                <div className="text-xs sm:text-sm text-gray-600">Gärtnermeister</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs sm:text-sm text-gray-600">
                {formatNumber(levelProgress?.currentXP || 0)} / {formatNumber(levelProgress?.nextLevelXP || 100)} XP
              </div>
              <div className="text-xs text-purple-600 font-medium">📊 Details</div>
            </div>
          </div>
          
          {/* XP Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
            <motion.div
              className="bg-gradient-to-r from-purple-400 to-purple-600 h-2 sm:h-3 rounded-full"
              style={{ width: `${(levelProgress?.progress || 0) * 100}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${(levelProgress?.progress || 0) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </motion.div>

        {/* Ressourcen - Vertikal für Mobile */}
        {/* Entfernt: Mobile Ressourcenleiste, da Edelstein-Video nur in der oberen Leiste sein soll */}

        {/* Ressourcen - Horizontal für Desktop */}
        <div className="hidden sm:grid grid-cols-3 gap-3">
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <div className="text-xl mb-1">💰</div>
            <div className="text-sm font-medium text-gray-600">Münzen</div>
            <div className="text-base font-bold text-yellow-700">{formatNumber(coins || 0)}</div>
          </div>
          
          <div className={`bg-purple-50 rounded-lg p-3 text-center transition-colors ${
            isGemVideoAvailable 
              ? 'cursor-pointer hover:bg-purple-100' 
              : 'opacity-60 cursor-not-allowed'
          }`} 
          onClick={isGemVideoAvailable ? handleGemClick : undefined} 
          title={isGemVideoAvailable ? `Werbevideo für ${formatNumber(calculateGemReward())} Edelsteine ansehen` : `Nächstes Video in ${getCooldownTime()} verfügbar`}>
            <div className={`text-xl mb-1 ${isGemVideoAvailable ? 'animate-pulse' : ''}`}>💎</div>
            <div className="text-sm font-medium text-gray-600">Edelsteine</div>
            <div className="text-base font-bold text-purple-700 truncate">{formatNumber(gems || 0)}</div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-xl mb-1">🔄</div>
            <div className="text-sm font-medium text-gray-600">Pro Sek.</div>
            <div className="text-base font-bold text-blue-700">{formatNumber(coinsPerSecond || 0)}</div>
          </div>
        </div>

        {/* Aktive Begleiter - Klappbar */}
        {activeCompanionsCount > 0 && (
          <div>
            <motion.button
              onClick={() => setShowCompanions(!showCompanions)}
              className="w-full p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 hover:from-blue-100 hover:to-purple-100 transition-colors"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🐾</span>
                  <div className="text-left">
                    <div className="font-bold text-blue-700 text-sm sm:text-base">Aktive Begleiter</div>
                    <div className="text-xs text-blue-600">{activeCompanionsCount}/{getMaxCompanionSlots()} aktiv</div>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: showCompanions ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-blue-600"
                >
                  ▼
                </motion.div>
              </div>
            </motion.button>
            
            <AnimatePresence>
              {showCompanions && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-2 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                    <div className="space-y-2">
                      {companions?.active?.filter(c => c).map((companion, index) => (
                        <motion.div 
                          key={companion.id} 
                          className="flex items-center gap-3 bg-white/70 rounded-lg p-2 sm:p-3 cursor-pointer hover:bg-white/90 transition-colors"
                          onClick={() => {
                            setSelectedCompanion(companion);
                            setSelectedCompanionSlot(index + 1);
                          }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          title="📊 Klicken für detaillierte Statistiken"
                        >
                          <div className="text-xl sm:text-2xl">
                            {companion.icon.startsWith('data:') ? (
                              <img 
                                src={companion.icon} 
                                alt={companion.name}
                                className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
                                style={{ display: 'inline-block' }}
                              />
                            ) : (
                              companion.icon
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-blue-700 text-sm sm:text-base truncate">{companion.name}</div>
                            <div className="text-xs sm:text-sm text-blue-600 capitalize">
                              {companion.type === 'legendary' ? 'Legendär' : 
                               companion.type === 'epic' ? 'Episch' : 
                               companion.type === 'rare' ? 'Selten' : 'Gewöhnlich'}
                            </div>
                          </div>
                          <div className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                            #{index + 1}
                          </div>
                          <div className="text-xs text-blue-500 opacity-60">📊</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Aktive Power-Ups - Klappbar für Mobile */}
        <div>
          <motion.button
            onClick={() => setShowPowerUps(!showPowerUps)}
            className="w-full p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200 hover:from-orange-100 hover:to-red-100 transition-colors"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg">🔥</span>
                <div className="text-left">
                  <div className="font-bold text-orange-700 text-sm sm:text-base">Power-Ups</div>
                  <div className="text-xs text-orange-600">
                    {activePowerUpsCount > 0 ? `${activePowerUpsCount} aktiv` : 'Keine aktiv'}
                  </div>
                </div>
              </div>
              <motion.div
                animate={{ rotate: showPowerUps ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="text-orange-600"
              >
                ▼
              </motion.div>
            </div>
          </motion.button>
          
          <AnimatePresence>
            {showPowerUps && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="mt-2 p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
                  {activePowerUpsCount > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {/* Basis Power-Ups */}
                      {powerUps?.goldRush?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">💰 Gold Rush</div>
                          <div className="text-xs">{formatTime(powerUps?.goldRush?.timeLeft || 0)}</div>
                        </motion.div>
                      )}
                      
                      {powerUps?.speedBoost?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-green-400 to-blue-400 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">⚡ Speed Boost</div>
                          <div className="text-xs">{formatTime(powerUps?.speedBoost?.timeLeft || 0)}</div>
                        </motion.div>
                      )}
                      
                      {powerUps?.luckyCharm?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-purple-400 to-pink-400 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">🍀 Lucky Charm</div>
                          <div className="text-xs">{formatTime(powerUps?.luckyCharm?.timeLeft || 0)}</div>
                        </motion.div>
                      )}

                      {/* Erweiterte Power-Ups */}
                      {powerUps?.megaMultiplier?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">🚀 Mega-Mult.</div>
                          <div className="text-xs">{formatTime(powerUps?.megaMultiplier?.timeLeft || 0)}</div>
                        </motion.div>
                      )}

                      {powerUps?.gemStorm?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">💎 Gem Storm</div>
                          <div className="text-xs">{formatTime(powerUps?.gemStorm?.timeLeft || 0)}</div>
                        </motion.div>
                      )}

                      {powerUps?.timeWarp?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">⏰ Time Warp</div>
                          <div className="text-xs">{formatTime(powerUps?.timeWarp?.timeLeft || 0)}</div>
                        </motion.div>
                      )}

                      {powerUps?.gardenBlessing?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">🌿 Garden Bless.</div>
                          <div className="text-xs">{formatTime(powerUps?.gardenBlessing?.timeLeft || 0)}</div>
                        </motion.div>
                      )}

                      {powerUps?.clickFrenzy?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">🔥 Click Frenzy</div>
                          <div className="text-xs">{formatTime(powerUps?.clickFrenzy?.timeLeft || 0)}</div>
                        </motion.div>
                      )}

                      {powerUps?.diamondMidas?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">✨ Diamond Midas</div>
                          <div className="text-xs">{formatTime(powerUps?.diamondMidas?.timeLeft || 0)}</div>
                        </motion.div>
                      )}

                      {powerUps?.infiniteEnergy?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg p-2 text-center"
                        >
                          <div className="font-bold text-sm">🌟 Infinite Energy</div>
                          <div className="text-xs">{formatTime(powerUps?.infiniteEnergy?.timeLeft || 0)}</div>
                        </motion.div>
                      )}

                      {/* Premium Power-Ups */}
                      {powerUps?.cosmicBoost?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg p-2 text-center border-2 border-yellow-300"
                        >
                          <div className="font-bold text-sm">🌌 Cosmic Boost</div>
                          <div className="text-xs">{formatTime(powerUps?.cosmicBoost?.timeLeft || 0)}</div>
                        </motion.div>
                      )}

                      {powerUps?.dragonPower?.isActive && (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="bg-gradient-to-r from-red-600 to-orange-700 text-white rounded-lg p-2 text-center border-2 border-gold animate-pulse"
                        >
                          <div className="font-bold text-sm">🐉 Dragon Power</div>
                          <div className="text-xs">{formatTime(powerUps?.dragonPower?.timeLeft || 0)}</div>
                        </motion.div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 text-sm py-4">
                      Keine aktiven Power-Ups
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Stats Overlay */}
      <StatsOverlay 
        isOpen={showStatsOverlay} 
        onToggle={() => setShowStatsOverlay(!showStatsOverlay)} 
        onOpenSettings={onOpenSettings} 
      />

      {/* Companion Stats Modal */}
      <CompanionStatsModal
        isOpen={!!selectedCompanion}
        onClose={() => setSelectedCompanion(null)}
        companion={selectedCompanion}
        slotNumber={selectedCompanionSlot}
      />

      {/* Video-Reward-Modal für Edelsteine wird entfernt, da zentral in App.tsx */}
    </div>
  );
}; 